import { useState } from "react";
import PlacesAutocomplete from "react-places-autocomplete";
import { LocationOn } from "@mui/icons-material";
import type { TFieldValue } from "../types/form.types";

export interface AddressData {
  address: string;
  city: string;
  state: string;
  zipCode: string;
  country: string;
  countryCode?: string;
  latitude?: number;
  longitude?: number;
}

interface AddressInputProps {
  id: string;
  value: string;
  placeholder?: string;
  disabled?: boolean;
  hasError: boolean;

  onChange: (value: TFieldValue) => void;
  onBlur: () => void;

  onAddressSelect?: (addressData: AddressData) => void;

  autocompleteOptions?: google.maps.places.AutocompleteOptions;

  style?: React.CSSProperties;
}

export function AddressInput({
  id,
  value,
  placeholder = "Enter street address, city, or postal code",
  disabled,
  hasError,
  onChange,
  onBlur,
  onAddressSelect,
  autocompleteOptions,
  style,
}: AddressInputProps) {
  const [isFocused, setIsFocused] = useState(false);

  const getAddressComponent = (components: google.maps.GeocoderAddressComponent[], types: string[]) => {
    for (const type of types) {
      const component = components.find((item) => item.types.includes(type));

      if (component) {
        return component.long_name;
      }
    }

    return "";
  };

  const handlePlaceSelected = (selectedAddress: string, placeId: string) => {
    const service = new window.google.maps.places.PlacesService(document.createElement("div"));

    service.getDetails({ placeId, fields: ["address_components", "formatted_address", "geometry"] }, (place, status) => {
      if (status !== window.google.maps.places.PlacesServiceStatus.OK || !place) return;

      const components = place.address_components || [];
      const formattedAddress = place.formatted_address || selectedAddress;

      /*
       * Street
       */
      const streetNumber = getAddressComponent(components, ["street_number"]);

      const streetName = getAddressComponent(components, ["route"]);

      /*
       * City
       *
       * Dependiendo del país Google puede devolver
       * la ciudad en diferentes componentes.
       */
      const city = getAddressComponent(components, ["locality", "postal_town", "administrative_area_level_2", "sublocality", "sublocality_level_1"]);

      /*
       * State
       */
      const state = getAddressComponent(components, ["administrative_area_level_1"]);

      /*
       * ZIP
       */
      const zipCode = getAddressComponent(components, ["postal_code"]);

      /*
       * Country
       */
      const country = getAddressComponent(components, ["country"]);

      /*
       * Country code
       */
      const countryComponent = components.find((component) => component.types.includes("country"));

      const countryCode = countryComponent?.short_name || "";

      /*
       * Dirección limpia:
       *
       * 6373 Co Rd 18
       *
       * Si Google no devuelve street_number / route,
       * usamos formatted_address.
       */
      const streetAddress = `${streetNumber} ${streetName}`.trim();

      const address = streetAddress || formattedAddress;

      const latitude = place.geometry?.location?.lat();

      const longitude = place.geometry?.location?.lng();

      const addressData: AddressData = { address, city, state, zipCode, country, countryCode, latitude, longitude };
      onChange(formattedAddress || address);
      onAddressSelect?.(addressData);
    });
  };

  const inputStyle: React.CSSProperties = {
    ...style,

    width: "100%",
    height: style?.height || "48px",

    boxSizing: "border-box",

    border: "1px solid #FB8E00",

    borderRadius: style?.borderRadius || "8px",

    outline: "none",

    paddingLeft: "42px",

    paddingRight: "14px",

    color: style?.color || "var(--fx-surface-text, #0F172A)",

    backgroundColor: style?.backgroundColor || "var(--fx-surface-base, #FFFFFF)",

    transition: "border-color 0.2s ease, box-shadow 0.2s ease",

    boxShadow: isFocused && !hasError ? "0 0 0 2px rgba(255, 194, 102, 0.25)" : "none",
  };

  const apiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;

  /*
   * Si no existe API key, evitamos que el componente
   * falle silenciosamente.
   */
  if (!apiKey) {
    console.error("[AddressInput] REACT_APP_GOOGLE_MAPS_API_KEY is not defined.");

    return (
      <div
        style={{
          position: "relative",
          width: "100%",
        }}
      >
        <LocationOn
          sx={{
            position: "absolute",
            left: "12px",
            top: "50%",
            transform: "translateY(-50%)",
            color: "#FB8E00",
            fontSize: "20px",
            zIndex: 1,
          }}
        />

        <input
          id={id}
          type="text"
          value={value}
          placeholder={placeholder}
          disabled={disabled}
          onChange={(e) => onChange(e.target.value)}
          onBlur={onBlur}
          onFocus={() => setIsFocused(true)}
          style={inputStyle}
        />
      </div>
    );
  }

  return (
    <PlacesAutocomplete
      value={value}
      onChange={(nextValue) => onChange(nextValue)}
      onSelect={handlePlaceSelected}
      searchOptions={autocompleteOptions}
    >
      {({ getInputProps, suggestions, getSuggestionItemProps, loading }) => (
        <div style={{ position: "relative", width: "100%" }}>
          <LocationOn
            sx={{
              position: "absolute",
              left: "12px",
              top: "50%",
              transform: "translateY(-50%)",

              color: hasError ? "#EF4444" : "#FB8E00",

              fontSize: "20px",

              zIndex: 2,

              pointerEvents: "none",
            }}
          />

          <input
            id={id}
            type="text"
            className={`df-input${hasError ? " df-input--error" : ""}`}
            placeholder={placeholder}
            disabled={disabled}
            autoComplete="off"
            {...getInputProps({
              onFocus: () => setIsFocused(true),
              onBlur: () => {
                setIsFocused(false);
                onBlur();
              },
            })}
            style={inputStyle}
          />
          {(loading || suggestions.length > 0) && (
            <div
              style={{
                position: "absolute",
                zIndex: 10,
                top: "100%",
                left: 0,
                right: 0,
                marginTop: "4px",
                maxHeight: "220px",
                overflowY: "auto",
                backgroundColor: "var(--fx-surface-base, #FFFFFF)",
                borderRadius: "4px",
                boxShadow: "0 2px 8px rgba(15, 23, 42, 0.18)",
              }}
            >
              {loading && <div style={{ padding: "8px 12px", color: "#64748B" }}>Loading...</div>}
              {suggestions.map((suggestion) => (
                <div
                  key={suggestion.placeId}
                  {...getSuggestionItemProps(suggestion, {
                    style: {
                      padding: "8px 12px",
                      cursor: "pointer",
                      backgroundColor: suggestion.active ? "var(--fx-surface-muted, #F1F5F9)" : "var(--fx-surface-base, #FFFFFF)",
                    },
                  })}
                >
                  {suggestion.description}
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </PlacesAutocomplete>
  );
}
